import React, { useState } from 'react';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import FormControlLabel from '@mui/material/FormControlLabel';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { IAdminUser, useAdminUsersQuery } from '../../api/admin';

export interface ShareableItem {
	id: number;
	name: string;
}

interface Props {
	open: boolean;
	onClose: () => void;
	/** The items being shared (watchlists, views, worksheets, etc.) */
	items: ShareableItem[];
	/** Singular label used in UI copy, e.g. "watchlist", "view", "worksheet" */
	resourceLabel: string;
	/** Excluded from the user picker — typically the owner of the source resources */
	sourceUserId: number;
	/** Show the "share anyway" checkbox that bypasses the watchlist / dataset permission checks. */
	allowSkipChecks?: boolean;
	/**
	 * Called when the user confirms the share.
	 * Receives the ids of the selected items, selected target users,
	 * and a map of item id -> new name for any renamed items.
	 * Should throw on failure so the dialog can display the error.
	 */
	onShare: (
		itemIds: number[],
		targetUserIds: number[],
		newNames: Record<number, string>,
		overwrite: boolean,
		skipChecks: boolean,
	) => Promise<void>;
}

function plural(label: string, count: number) {
	return count === 1 ? label : `${label}s`;
}

export default function ShareResourceDialog({
	open,
	onClose,
	items,
	resourceLabel,
	sourceUserId,
	allowSkipChecks = false,
	onShare,
}: Props) {
	const { data: allUsers, isLoading: usersLoading } = useAdminUsersQuery();

	const [step, setStep] = useState<'select' | 'confirm'>('select');
	const [selectedUsers, setSelectedUsers] = useState<IAdminUser[]>([]);
	const [newNames, setNewNames] = useState<Record<number, string>>({});
	const [overwrite, setOverwrite] = useState(false);
	const [skipChecks, setSkipChecks] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [succeeded, setSucceeded] = useState(false);

	const eligibleUsers = (allUsers ?? []).filter((u) => u.is_active && u.id !== sourceUserId);
	const totalShares = items.length * selectedUsers.length;
	const labelCap = resourceLabel.charAt(0).toUpperCase() + resourceLabel.slice(1);

	function resetAndClose() {
		setStep('select');
		setSelectedUsers([]);
		setNewNames({});
		setOverwrite(false);
		setSkipChecks(false);
		setError(null);
		setSucceeded(false);
		onClose();
	}

	async function handleShare() {
		setSubmitting(true);
		setError(null);
		try {
			// Only pass entries where the user actually typed a non-empty name
			const effectiveNames: Record<number, string> = {};
			for (const [id, name] of Object.entries(newNames)) {
				const trimmed = name.trim();
				if (trimmed) {
					effectiveNames[Number(id)] = trimmed;
				}
			}
			await onShare(
				items.map((i) => i.id),
				selectedUsers.map((u) => u.id),
				effectiveNames,
				overwrite,
				skipChecks,
			);
			setSucceeded(true);
		} catch (e: any) {
			setError(e?.response?.data?.detail ?? 'An unexpected error occurred. Please try again.');
		} finally {
			setSubmitting(false);
		}
	}

	return (
		<Dialog
			open={open}
			onClose={resetAndClose}
			maxWidth="sm"
			fullWidth
			PaperProps={{ sx: { backgroundImage: 'none' } }}
		>
			{succeeded ? (
				<>
					<DialogTitle>Share Complete</DialogTitle>
					<DialogContent>
						<Alert severity="success">
							Successfully shared {items.length} {plural(resourceLabel, items.length)} with{' '}
							{selectedUsers.length} {plural('user', selectedUsers.length)}.
						</Alert>
					</DialogContent>
					<DialogActions>
						<Button variant="contained" onClick={resetAndClose}>
							Done
						</Button>
					</DialogActions>
				</>
			) : step === 'select' ? (
				<>
					<DialogTitle>Share {labelCap}s</DialogTitle>
					<DialogContent sx={{ pt: 2 }}>
						<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
							Sharing{' '}
							<strong>
								{items.length} {plural(resourceLabel, items.length)}
							</strong>
							. Select one or more users to share with.
						</Typography>
						<Autocomplete
							multiple
							disableCloseOnSelect
							filterSelectedOptions
							options={eligibleUsers}
							loading={usersLoading}
							getOptionLabel={(option) =>
								option.full_name ? `${option.full_name} (${option.email})` : option.email
							}
							isOptionEqualToValue={(option, value) => option.id === value.id}
							value={selectedUsers}
							onChange={(_, newValue) => setSelectedUsers(newValue)}
							renderTags={(value, getTagProps) =>
								value.map((option, index) => (
									<Chip
										size="small"
										label={option.full_name ?? option.email}
										{...getTagProps({ index })}
									/>
								))
							}
							renderInput={(params) => (
								<TextField
									{...params}
									label="Select users"
									placeholder={selectedUsers.length === 0 ? 'Search by name or email…' : ''}
									InputProps={{
										...params.InputProps,
										endAdornment: (
											<>
												{usersLoading ? <CircularProgress size={16} /> : null}
												{params.InputProps.endAdornment}
											</>
										),
									}}
								/>
							)}
						/>
					</DialogContent>
					<DialogActions>
						<Button onClick={resetAndClose}>Cancel</Button>
						<Button
							variant="contained"
							onClick={() => setStep('confirm')}
							disabled={selectedUsers.length === 0}
						>
							Next
						</Button>
					</DialogActions>
				</>
			) : (
				<>
					<DialogTitle>Confirm Share</DialogTitle>
					<DialogContent sx={{ pt: 1 }}>
						{error && (
							<Alert severity="error" sx={{ mb: 2 }}>
								{error}
							</Alert>
						)}
						<Typography variant="body2" color="text.secondary" gutterBottom>
							This will create <strong>{totalShares}</strong>{' '}
							{totalShares === 1 ? 'copy' : 'copies'} — one for each user listed below.
						</Typography>

						<Box sx={{ mt: 2 }}>
							<Typography variant="subtitle2" gutterBottom>
								{labelCap}s ({items.length})
							</Typography>
							<List dense disablePadding>
								{items.map((item) => (
									<ListItem key={item.id} disableGutters sx={{ py: 0.5 }}>
										<ListItemText
											primary={item.name}
											primaryTypographyProps={{ variant: 'body2' }}
											sx={{ flex: '0 0 auto', mr: 2, minWidth: 0 }}
										/>
										<TextField
											size="small"
											variant="outlined"
											placeholder={item.name}
											value={newNames[item.id] ?? ''}
											onChange={(e) =>
												setNewNames((prev) => ({
													...prev,
													[item.id]: e.target.value,
												}))
											}
											inputProps={{ style: { padding: '4px 8px' } }}
											sx={{ flex: 1 }}
											label="Rename (optional)"
										/>
									</ListItem>
								))}
							</List>
						</Box>

						<FormControlLabel
							control={
								<Checkbox
									checked={overwrite}
									onChange={(e) => setOverwrite(e.target.checked)}
									size="small"
								/>
							}
							label={
								<Typography variant="body2">
									Overwrite (if previously shared)
								</Typography>
							}
							sx={{ mt: 1 }}
						/>
						{allowSkipChecks && (
							<FormControlLabel
								control={
									<Checkbox
										checked={skipChecks}
										onChange={(e) => setSkipChecks(e.target.checked)}
										size="small"
									/>
								}
								label={
									<Typography variant="body2">
										Share anyway (skip watchlist and dataset permission checks)
									</Typography>
								}
								sx={{ display: 'flex' }}
							/>
						)}

						<Divider sx={{ my: 2 }} />

						<Box>
							<Typography variant="subtitle2" gutterBottom>
								Recipients ({selectedUsers.length})
							</Typography>
							<List dense disablePadding>
								{selectedUsers.map((u) => (
									<ListItem key={u.id} disableGutters sx={{ py: 0 }}>
										<ListItemText
											primary={u.full_name ?? u.email}
											secondary={u.full_name ? u.email : undefined}
											primaryTypographyProps={{ variant: 'body2' }}
											secondaryTypographyProps={{ variant: 'caption' }}
										/>
									</ListItem>
								))}
							</List>
						</Box>
					</DialogContent>
					<DialogActions>
						<Button onClick={() => { setError(null); setStep('select'); }}>Back</Button>
						<Button onClick={resetAndClose}>Cancel</Button>
						<Button
							variant="contained"
							onClick={handleShare}
							disabled={submitting}
							startIcon={submitting ? <CircularProgress size={16} /> : undefined}
						>
							{submitting ? 'Sharing…' : 'Share'}
						</Button>
					</DialogActions>
				</>
			)}
		</Dialog>
	);
}
